// Report generation flow — ports psychologist.js _openReportFlow / _setReportStep
// / _submitReportGeneration / _pollReportJob.
//
// Flow: check /report/status → 3 steps (intro → topic → exclusions) → POST
// /report/generate → if pending, poll /report/job/{id} every 3s (≤5 min) until
// completed/failed. On success: navigate to /report and show the session popup.

import { useEffect, useRef, useState } from 'react'
import { reportApi } from '../../api/report'
import { ApiError, messageFromError } from '../../api/client'
import { toast } from '../../store/toastStore'
import * as metrica from '../../lib/metrica'
import styles from './Psychologist.module.css'

const POLL_MS = 3000
const MAX_MS = 5 * 60 * 1000

interface ReportFlowModalProps {
  onClose: () => void
  onCompleted?: () => void
  onShowExisting?: () => void
}

export default function ReportFlowModal({ onClose, onCompleted, onShowExisting }: ReportFlowModalProps) {
  const [phase, setPhase] = useState('loading') // loading | step | submitting | error
  const [step, setStep] = useState(1)
  const [topic, setTopic] = useState('')
  const [exclusions, setExclusions] = useState('')
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const startedRef = useRef(0)

  const stopPoll = () => {
    if (pollRef.current) clearInterval(pollRef.current)
    pollRef.current = null
  }

  // Rate-limit / existing-report check on open.
  useEffect(() => {
    metrica.reachGoal('psychologist_report_click')
    let cancelled = false
    reportApi
      .status()
      .then((data) => {
        if (cancelled) return
        if (!data.can_generate) {
          if (data.last_report_date) {
            onShowExisting?.()
            toast('Показываем ваш предыдущий отчёт', 'info')
          } else {
            const days = Math.ceil((data.remaining_seconds || 0) / 86400)
            toast(`Отчёт можно сформировать не чаще раза в 3 дня. Подождите ещё ${days} дн.`, 'info')
          }
          onClose()
        } else {
          setPhase('step')
        }
      })
      .catch(() => {
        if (!cancelled) {
          toast('Не удалось проверить доступность отчёта', 'error')
          onClose()
        }
      })
    return () => {
      cancelled = true
      stopPoll()
    }
  }, [onClose, onShowExisting])

  const pollJob = (jobId: string) => {
    stopPoll()
    startedRef.current = Date.now()
    const tick = async () => {
      if (Date.now() - startedRef.current > MAX_MS) {
        stopPoll()
        setPhase('step')
        setStep(3)
        toast('Превышено время ожидания. Попробуйте ещё раз.', 'error')
        return
      }
      try {
        const data = await reportApi.job(jobId)
        if (data.status === 'completed') {
          stopPoll()
          metrica.reachGoal('psychologist_report_generated')
          onCompleted?.()
        } else if (data.status === 'failed') {
          stopPoll()
          setPhase('step')
          setStep(3)
          toast(data.error || 'Не удалось сформировать отчёт', 'error')
        }
        // pending → keep polling
      } catch (err) {
        // 404 = the job vanished (server restarted mid-generation). Don't poll
        // to the 5-min timeout — try to surface an already-generated report
        // instead (original psychologist.js:293-305).
        if (err instanceof ApiError && err.status === 404) {
          stopPoll()
          onShowExisting?.()
          toast('Показываем ваш предыдущий отчёт', 'info')
          onClose()
          return
        }
        /* other errors are transient — keep polling until hard timeout */
      }
    }
    tick()
    pollRef.current = setInterval(tick, POLL_MS)
  }

  const submit = async () => {
    setPhase('submitting')
    try {
      const data = await reportApi.generate({ sessionQuestion: topic, exclusions })
      if (data.status === 'cached') {
        onShowExisting?.()
        toast('Показываем ваш предыдущий отчёт', 'info')
        onClose()
        return
      }
      if (data.status === 'pending' && data.job_id) {
        pollJob(data.job_id)
        return
      }
      // Legacy inline 'generated'
      metrica.reachGoal('psychologist_report_generated')
      onCompleted?.()
    } catch (err) {
      setPhase('step')
      setStep(3)
      toast(messageFromError(err, 'Не удалось сформировать отчёт'), 'error')
    }
  }

  const dots = (n: number) => (
    <div className={styles.dots}>
      {[1, 2, 3].map((i) => (
        <span key={i} className={i <= n ? styles.dotActive : styles.dot} />
      ))}
    </div>
  )

  let body
  if (phase === 'loading' || phase === 'submitting') {
    body = (
      <div className={styles.loadingBox}>
        <div className={styles.spinner} />
        {phase === 'submitting' && <p>Формируем отчёт…</p>}
      </div>
    )
  } else if (step === 1) {
    body = (
      <>
        {dots(1)}
        <h2>Отчёт для психолога</h2>
        <div className={styles.warnBox}>
          <p>Отчёт можно сформировать <strong>один раз в 3 дня</strong>. После генерации он откроется в приложении и будет доступен психологу по вашему решению.</p>
          <p className={styles.warnPrivate}>Прямая речь пользователя цитируется дословно. Если хотите что-то исключить — укажите на следующем шаге.</p>
        </div>
        <button className={styles.primaryBtn} onClick={() => setStep(2)}>Далее</button>
        <button className={styles.tertiaryBtn} onClick={onClose}>Отмена</button>
      </>
    )
  } else if (step === 2) {
    body = (
      <>
        {dots(2)}
        <h2>Тема сессии</h2>
        <p className={styles.fieldHint}>Что хотите вынести на сессию с психологом? Можно оставить пустым.</p>
        <textarea
          className={styles.textarea}
          maxLength={500}
          placeholder="Например: тревога перед публичными выступлениями"
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
        />
        <button className={styles.primaryBtn} onClick={() => setStep(3)}>Далее</button>
        <button className={styles.tertiaryBtn} onClick={() => setStep(1)}>Назад</button>
      </>
    )
  } else {
    body = (
      <>
        {dots(3)}
        <h2>Что исключить</h2>
        <p className={styles.fieldHint}>Темы, цитаты или периоды, которые не должны попасть в отчёт. Можно оставить пустым.</p>
        <textarea
          className={styles.textarea}
          maxLength={500}
          placeholder="Например: не упоминать конфликты на работе"
          value={exclusions}
          onChange={(e) => setExclusions(e.target.value)}
        />
        <button className={styles.primaryBtn} onClick={submit}>Сформировать отчёт</button>
        <button className={styles.tertiaryBtn} onClick={() => setStep(2)}>Назад</button>
      </>
    )
  }

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.popup} onClick={(e) => e.stopPropagation()}>
        {body}
      </div>
    </div>
  )
}
